/**
 * Site Header JavaScript
 * Handles desktop megamenu, mobile menu, and search functionality
 */

class SiteHeader {
  constructor() {
    this.init();
  }

  init() {
    // Initialize megamenu functionality
    this.initMegamenu();
    
    // Initialize mobile menu functionality
    this.initMobileMenu();
    
    // Initialize search functionality
    this.initSearch();

    // Initialize collapsed/minimised header state behavior
    this.initHeaderStates();

    // Initialize predictive search dropdown/drawer logic
    this.initPredictiveSearch();
  }

  /**
   * Initialize desktop megamenu functionality
   */
  initMegamenu() {
    const megamenuTriggers = document.querySelectorAll('[data-megamenu-trigger]');
    const megamenu = document.querySelector('[data-megamenu]');
    const megamenuPanels = document.querySelectorAll('[data-megamenu-panel]');
    const megamenuTabs = document.querySelectorAll('[data-megamenu-tab]');
    const megamenuOverlay = document.querySelector('[data-megamenu-overlay]');
    // Ensure overlay lives at the document root so it doesn't inherit the header stacking context
    if (megamenuOverlay && megamenuOverlay.parentElement !== document.body) {
      document.body.appendChild(megamenuOverlay);
    }

    if (!megamenu || megamenuTriggers.length === 0) {
      return;
    }

    let activeTrigger = null;
    let activePanel = null;
    let hoverTimeout = null;

    // Handle megamenu trigger hover/click
    megamenuTriggers.forEach(trigger => {
      const triggerHandle = trigger.getAttribute('data-megamenu-trigger');
      let panel = document.querySelector(`[data-megamenu-panel="${triggerHandle}"]`);

      // Fallback 1: aria-controls id
      if (!panel) {
        const controlsId = trigger.getAttribute('aria-controls');
        if (controlsId) {
          panel = document.getElementById(controlsId);
        }
      }

      // Fallback 2: try raw title and a sanitized handle
      if (!panel) {
        const rawTitle = (trigger.textContent || '').trim();
        if (rawTitle) {
          const sanitized = rawTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          panel = document.querySelector(`[data-megamenu-panel="${rawTitle}"]`) ||
                  document.querySelector(`[data-megamenu-panel="${sanitized}"]`);
        }
      }

      if (!panel) return;

      // Click - toggle megamenu
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMegamenu(trigger, panel);
      });
    });

    if (megamenuOverlay) {
      megamenuOverlay.addEventListener('click', () => {
        this.hideMegamenu();
      });
    }

    // Also delegate tab clicks inside the megamenu to be robust
    megamenu.addEventListener('click', (e) => {
      const tab = e.target?.closest('[data-megamenu-tab]');
      if (!tab) return;
      e.preventDefault();
      this.clearHideTimeout();
      this.switchMegamenuTab(tab);
    });

    // Store methods for external access
    this.showMegamenu = (trigger, panel) => {
      this.clearHideTimeout();
      
      // Hide current active panel
      if (activePanel && activePanel !== panel) {
        activePanel.style.display = 'none';
      }
      
      // Show new panel
      panel.style.display = 'grid';
      // Ensure the first tab/content is shown within this panel
      const panelTabs = panel.querySelectorAll('[data-megamenu-tab]');
      const panelContents = panel.querySelectorAll('[data-megamenu-content]');
      if (panelContents.length) {
        panelContents.forEach(c => { c.style.display = 'none'; });
        panelContents[0].style.display = 'grid';
      }
      if (panelTabs.length) {
        panelTabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        panelTabs[0].classList.add('is-active');
        panelTabs[0].setAttribute('aria-selected', 'true');
      }
      megamenu.style.opacity = '';
      megamenu.style.visibility = '';
      megamenu.style.display = '';
      megamenu.classList.add('active');
      if (megamenuOverlay) {
        megamenuOverlay.style.opacity = '';
        megamenuOverlay.style.visibility = '';
        megamenuOverlay.classList.add('active');
        megamenuOverlay.setAttribute('aria-hidden', 'false');
      }
      
      // Update active states
      if (activeTrigger) {
        activeTrigger.classList.remove('is-active');
        activeTrigger.setAttribute('aria-expanded', 'false');
      }
      
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      
      activeTrigger = trigger;
      activePanel = panel;
      
      // Prevent page scroll when mega menu is open
      document.documentElement.setAttribute('scroll-lock', '');
    };

    this.hideMegamenu = () => {
      megamenu.classList.remove('active');
      megamenu.style.opacity = '0';
      megamenu.style.visibility = 'hidden';
      megamenu.style.display = 'none';
      if (megamenuOverlay) {
        megamenuOverlay.classList.remove('active');
        megamenuOverlay.style.opacity = '0';
        megamenuOverlay.style.visibility = 'hidden';
        megamenuOverlay.setAttribute('aria-hidden', 'true');
      }
      
      if (activePanel) {
        activePanel.style.display = 'none';
      }
      
      if (activeTrigger) {
        activeTrigger.classList.remove('is-active');
        activeTrigger.setAttribute('aria-expanded', 'false');
      }
      
      activeTrigger = null;
      activePanel = null;
      
      // Restore page scroll when mega menu is closed
      document.documentElement.removeAttribute('scroll-lock');
    };

    this.hideMegamenuDelayed = () => {
      this.clearHideTimeout();
      hoverTimeout = setTimeout(() => {
        this.hideMegamenu();
      }, 150); // Small delay to prevent flickering
    };

    this.clearHideTimeout = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
    };

    this.toggleMegamenu = (trigger, panel) => {
      if (activePanel === panel) {
        this.hideMegamenu();
      } else {
        this.showMegamenu(trigger, panel);
      }
    };

    this.switchMegamenuTab = (tab) => {
      const tabIndex = tab.getAttribute('data-megamenu-tab');
      const parentPanel = tab.closest('[data-megamenu-panel]') || document;
      const contentPanel = parentPanel.querySelector(`[data-megamenu-content="${tabIndex}"]`);
      if (!contentPanel) return;

      // Update tab states within this panel only
      const localTabs = parentPanel.querySelectorAll('[data-megamenu-tab]');
      localTabs.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      // Update content panels within this panel only
      const localContents = parentPanel.querySelectorAll('[data-megamenu-content]');
      localContents.forEach(p => {
        p.style.display = 'none';
      });
      contentPanel.style.display = 'grid';
    };
  }

  /**
   * header state behavior
   * - Collapsed: when scrolling down past threshold
   * - Minimised: when scrolling up (but not at top)
   */
  initHeaderStates() {
    const headerEl = document.getElementById('site-header');
    if (!headerEl) {
      return;
    }
    
    // Target the #header-group element (the fixed container that moves with top property)
    const headerGroupEl = document.getElementById('header-group');

    const statesAttr = headerEl.getAttribute('data-site-header-states') || '';
    const enableCollapsed = statesAttr.includes('collapsed');
    const enableMinimised = statesAttr.includes('minimised');
    
    if (!enableCollapsed && !enableMinimised) {
      return;
    }

    if (!headerGroupEl) {
      return;
    }

    // Cache element references
    const announcementSection = headerGroupEl.querySelector('.shopify-section:has(.announcement-bar)');
    const siteHeaderEl = headerGroupEl.querySelector('.site-header');
    
    // Calculate header heights for positioning without DOM manipulation
    const getHeaderHeights = () => {
      // Measure site header height (consistent regardless of state)
      const siteHeaderHeight = siteHeaderEl ? siteHeaderEl.offsetHeight : 0;
      
      // Measure announcement section height
      // Use scrollHeight to get natural height even when max-height: 0 is applied
      let announcementHeight = 0;
      if (announcementSection) {
        // If currently visible, use offsetHeight
        // If hidden, use scrollHeight to get natural height
        announcementHeight = announcementSection.offsetHeight || announcementSection.scrollHeight || 0;
      }
      
      // Account for margins - site-header has margin-block-start when announcement bar is visible
      const siteHeaderMarginTop = siteHeaderEl ? 
        parseFloat(getComputedStyle(siteHeaderEl).marginTop) || 0 : 0;
      
      // Total height = announcement section + site header + any margins
      const totalHeight = announcementHeight + siteHeaderHeight + siteHeaderMarginTop;
      
      // Get --spacing-m value (1rem = 16px) and convert to pixels
      const spacingMValue = getComputedStyle(document.documentElement).getPropertyValue('--spacing-m').trim();
      let spacingM = 16; // Default fallback
      if (spacingMValue) {
        // Handle rem values (1rem = 16px)
        if (spacingMValue.includes('rem')) {
          const remValue = parseFloat(spacingMValue);
          spacingM = remValue * 16; // Convert rem to px (assuming 16px base)
        } else if (spacingMValue.includes('px')) {
          spacingM = parseFloat(spacingMValue);
        } else {
          spacingM = parseFloat(spacingMValue) || 16;
        }
      }
      
      // Calculate translate values for positioning
      // Collapsed: translate up by full height to hide everything
      const collapsedTranslate = enableCollapsed ? -totalHeight : 0;
      // Minimised: translate down by spacing-m to position header with spacing from top
      // Announcement bar is hidden via CSS, so we just need spacing offset
      const minimisedTranslate = spacingM;
      
      return { collapsedTop: collapsedTranslate, minimisedTop: minimisedTranslate };
    };

    // Set initial header offset height once (don't update during scroll to avoid feedback loop)
    // Ensure we're not in a collapsed/minimised state before measuring
    headerGroupEl.classList.remove('is-collapsed', 'is-minimised');
    headerGroupEl.style.translate = '0 0';
    
    // Calculate the full natural height including announcement bar
    const { collapsedTop, minimisedTop } = getHeaderHeights();
    
    // Get the full natural height of the header group (includes announcement bar + site header + margins)
    const siteHeaderHeight = siteHeaderEl ? siteHeaderEl.offsetHeight : 0;
    const siteHeaderMarginTop = siteHeaderEl ? parseFloat(getComputedStyle(siteHeaderEl).marginTop) || 0 : 0;
    const announcementHeight = announcementSection ? (announcementSection.offsetHeight || announcementSection.scrollHeight || 0) : 0;
    const initialFullHeight = announcementHeight + siteHeaderHeight + siteHeaderMarginTop;
    
    document.body.style.setProperty('--header-group-height', `${initialFullHeight}px`);

    // Initialize scroll state tracking
    let scroll = {
      direction: 'none',
      header: 'open',
      top: true,
      y: window.scrollY,
    };

    const threshold = 2; // Threshold to reduce jitter while maintaining responsiveness
    const scrollStart = 10; // Small buffer at top before header changes
    const scrollHideDelay = 50; // Keep header visible for this many pixels when scrolling down

    let ticking = false;
    let lastAppliedState = null; // Track last applied state to prevent unnecessary updates
    
    const onScrollHandler = () => {
      // Recalculate heights on each scroll to account for resize changes
      const { collapsedTop, minimisedTop } = getHeaderHeights();
      
      const currentY = window.scrollY;
      const difference = currentY - scroll.y;

      // Skip if difference is less than threshold
      if (Math.abs(difference) < threshold) {
        ticking = false;
        return;
      }

      // Update scroll object with current state
      scroll = {
        direction: difference > 0 ? 'down' : 'up',
        header: 'open', // Will be updated below based on conditions
        top: currentY <= scrollStart,
        y: currentY,
      };

      // Correct header behavior:
      // - Scroll DOWN = header moves UP (collapsed/hidden) but only after scrollHideDelay
      // - Scroll UP = header moves DOWN (visible/minimised)
      // - At TOP = header fully visible (open)
      if (currentY <= scrollStart) {
        scroll.header = 'open'; // Header fully visible when at/near top
      } else if (scroll.direction === 'down' && currentY > scrollStart + scrollHideDelay) {
        scroll.header = 'collapsed'; // Header hides when scrolling down past delay threshold
      } else if (scroll.direction === 'up' && currentY > scrollStart) {
        scroll.header = 'minimised'; // Header shows when scrolling up but not at top
      } else if (scroll.direction === 'down' && currentY <= scrollStart + scrollHideDelay) {
        scroll.header = 'open'; // Keep header visible when scrolling down within delay threshold
      }
      
      // Only apply changes if state actually changed
      if (lastAppliedState === scroll.header) {
        ticking = false;
        return;
      }
      
      lastAppliedState = scroll.header;

      // Use requestAnimationFrame to ensure smooth transitions
      requestAnimationFrame(() => {
        // Apply positioning based on header state and settings
        // Use translate instead of top for smoother animations
        if (scroll.header === 'collapsed' && enableCollapsed) {
          headerGroupEl.style.translate = `0 ${collapsedTop}px`;
          headerGroupEl.classList.remove('is-minimised');
          headerGroupEl.classList.add('is-collapsed');
        } else if (scroll.header === 'minimised' && enableMinimised) {
          headerGroupEl.style.translate = `0 ${minimisedTop}px`;
          headerGroupEl.classList.remove('is-collapsed');
          headerGroupEl.classList.add('is-minimised');
        } else {
          headerGroupEl.style.translate = '0 0';
          headerGroupEl.classList.remove('is-collapsed', 'is-minimised');
        }
      });

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(onScrollHandler);
        ticking = true;
      }
    };

    // Recalculate and reapply header position on resize
    // DO NOT update --header-group-height (it stays fixed to prevent layout shifts)
    let resizeTicking = false;
    const onResizeHandler = () => {
      // Recalculate translate values with new dimensions
      const { collapsedTop, minimisedTop } = getHeaderHeights();
      
      // Reapply current header state with updated heights
      // Use translate instead of top for smoother animations
      if (scroll.header === 'collapsed' && enableCollapsed) {
        headerGroupEl.style.translate = `0 ${collapsedTop}px`;
      } else if (scroll.header === 'minimised' && enableMinimised) {
        headerGroupEl.style.translate = `0 ${minimisedTop}px`;
      } else {
        headerGroupEl.style.translate = '0 0';
      }
      
      resizeTicking = false;
    };

    const onResize = () => {
      if (!resizeTicking) {
        window.requestAnimationFrame(onResizeHandler);
        resizeTicking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    
    // Also listen for orientation changes
    window.addEventListener('orientationchange', () => {
      // Wait a bit for orientation change to complete
      setTimeout(() => {
        onResizeHandler();
      }, 100);
    });
  }

  /**
   * Initialize mobile menu functionality
   */
  initMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const menuClose = document.querySelector('[data-menu-close]');
    const mobileMenu = document.querySelector('.custom-mobile-menu');
    const mobileMenuOverlay = document.querySelector('.site-header__mobile-menu-overlay');
    const submenuTriggers = document.querySelectorAll('[data-mobile-submenu-trigger]');

    if (!menuToggle || !mobileMenu) {
      return;
    }

    // Toggle mobile menu
    menuToggle.addEventListener('click', (e) => {
      this.openMobileMenu();
    });

    if (menuClose) {
      menuClose.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    if (mobileMenuOverlay) {
      mobileMenuOverlay.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    // Handle submenu triggers (use event delegation for dynamic content)
    if (mobileMenu) {
      mobileMenu.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-mobile-submenu-trigger]');
        if (trigger) {
          e.preventDefault();
          this.openMobileSubdrawer(trigger);
        }

        const backButton = e.target.closest('[data-mobile-subdrawer-back]');
        if (backButton) {
          e.preventDefault();
          this.closeMobileSubdrawer(backButton.closest('[data-mobile-submenu]'));
        }

        const closeButton = e.target.closest('[data-mobile-subdrawer-close]');
        if (closeButton) {
          e.preventDefault();
          this.closeMobileMenu();
        }

        const topBarItem = e.target.closest('[data-top-bar-item]');
        if (topBarItem) {
          e.preventDefault();
          this.switchTopBarItem(topBarItem);
        }
      });
    }

    // Store scroll position for when menu opens
    let scrollPosition = 0;

    // Store methods for external access
    this.openMobileMenu = () => {
      // Save current scroll position
      scrollPosition = window.scrollY;
      
      mobileMenu.style.transform = '';
      mobileMenu.classList.add('active');
      if (mobileMenuOverlay) {
        mobileMenuOverlay.style.visibility = '';
        mobileMenuOverlay.style.opacity = '';
        mobileMenuOverlay.classList.add('active');
      }
      
      // Lock scroll and preserve position
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.style.top = `-${scrollPosition}px`;
    };

    this.closeMobileMenu = () => {
      // Close all subdrawers first
      document.querySelectorAll('[data-mobile-submenu].active').forEach(subdrawer => {
        this.closeMobileSubdrawer(subdrawer);
      });
      
      mobileMenu.classList.remove('active');
      mobileMenu.style.transform = 'translateX(-100%)';
      if (mobileMenuOverlay) {
        mobileMenuOverlay.classList.remove('active');
        mobileMenuOverlay.style.visibility = 'hidden';
        mobileMenuOverlay.style.opacity = '0';
      }
      
      // Unlock scroll and restore position
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    this.openMobileSubdrawer = (trigger) => {
      const submenuId = trigger.getAttribute('data-mobile-submenu-trigger');
      const subdrawer = document.querySelector(`[data-mobile-submenu="${submenuId}"]`);
      
      if (!subdrawer) {
        return;
      }

      subdrawer.style.transform = '';
      subdrawer.classList.add('active');
      subdrawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      
      // Reset scroll position to top for both tier 1 (main menu) and tier 2 (subdrawer)
      const mobileMenuContent = document.querySelector('.site-header__mobile-menu-content');
      if (mobileMenuContent) {
        mobileMenuContent.scrollTop = 0;
      }
      
      const subdrawerBody = subdrawer.querySelector('.site-header__mobile-subdrawer-body');
      if (subdrawerBody) {
        subdrawerBody.scrollTop = 0;
      }
    };

    this.closeMobileSubdrawer = (subdrawer) => {
      if (!subdrawer) return;

      subdrawer.classList.remove('active');
      subdrawer.style.transform = 'translateX(-100%)';
      subdrawer.setAttribute('aria-hidden', 'true');
      
      // Reset to first panel
      const firstPanel = subdrawer.querySelector('[data-panel-index="0"]');
      if (firstPanel) {
        subdrawer.querySelectorAll('[data-panel-index]').forEach(panel => {
          panel.classList.remove('is-active');
        });
        firstPanel.classList.add('is-active');
      }

      // Reset top bar items
      const firstTopBarItem = subdrawer.querySelector('[data-top-bar-item]');
      if (firstTopBarItem) {
        subdrawer.querySelectorAll('[data-top-bar-item]').forEach(item => {
          item.classList.remove('is-active');
        });
        firstTopBarItem.classList.add('is-active');
      }

      // Reset trigger aria-expanded
      const trigger = document.querySelector(`[data-mobile-submenu-trigger="${subdrawer.getAttribute('data-mobile-submenu')}"]`);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    };

    this.switchTopBarItem = (topBarItem) => {
      const parentHandle = topBarItem.getAttribute('data-parent-handle');
      const panelIndex = topBarItem.getAttribute('data-top-bar-item');
      const subdrawer = document.querySelector(`[data-mobile-submenu="${parentHandle}"]`);
      
      if (!subdrawer) return;

      // Update active top bar item
      subdrawer.querySelectorAll(`[data-parent-handle="${parentHandle}"][data-top-bar-item]`).forEach(item => {
        item.classList.remove('is-active');
      });
      topBarItem.classList.add('is-active');

      // Switch to corresponding panel
      subdrawer.querySelectorAll(`[data-parent-handle="${parentHandle}"][data-panel-index]`).forEach(panel => {
        panel.classList.remove('is-active');
      });
      const targetPanel = subdrawer.querySelector(`[data-panel-index="${panelIndex}"][data-parent-handle="${parentHandle}"]`);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
      }
    };
  }

  /**
   * Initialize search functionality
   */
  initSearch() {
    const searchInputs = document.querySelectorAll('.site-header__search-input, .site-header__mobile-search-input');
    const searchClearButtons = document.querySelectorAll('[data-search-clear], [data-mobile-search-clear]');
    const searchToggle = document.querySelector('[data-search-toggle]');

    // Handle search input changes
    searchInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.updateSearchClearButton(input);
      });
    });

    // Handle search clear buttons
    searchClearButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.clearSearch(button);
      });
    });

    // Handle search toggle (if exists)
    if (searchToggle) {
      searchToggle.addEventListener('click', (e) => {
        this.toggleSearch();
      });
    }

    // Store methods for external access
    this.updateSearchClearButton = (input) => {
      const clearButton = input.parentElement.querySelector('[data-search-clear], [data-mobile-search-clear]');
      if (clearButton) {
        clearButton.style.display = input.value.trim() ? 'block' : 'none';
      }
    };

    this.clearSearch = (button) => {
      const input = button.parentElement.querySelector('input[type="search"]');
      if (input) {
        input.value = '';
        input.focus();
        button.style.display = 'none';
      }
    };

    this.toggleSearch = () => {
      // Implementation for search toggle if needed
    };
  }

  /**
   * Initialize predictive search dropdown (desktop) and drawer (mobile)
   */
  /**
   * Initialize predictive search functionality (desktop dropdown and mobile drawer)
   */
  initPredictiveSearch() {
    const header = document.getElementById('site-header');
    if (!header) return;

    const desktopControls = this.#getDesktopSearchControls(header);
    const mobileControls = this.#getMobileSearchControls(header);

    let desktopHandlers = null;
    let mobileHandlers = null;

    if (desktopControls) {
      desktopHandlers = this.#initDesktopPredictiveSearch(desktopControls);
    }

    if (mobileControls) {
      mobileHandlers = this.#initMobilePredictiveSearch(mobileControls);
    }

    if (desktopHandlers && mobileHandlers) {
      this.#setupBreakpointHandler(desktopHandlers, mobileHandlers);
    }
  }

  /**
   * Get desktop search control elements
   * @private
   */
  #getDesktopSearchControls(header) {
    const searchForm = header?.querySelector('[data-js-header-search-form]');
    const searchInput = header?.querySelector('[data-js-header-search-input]');
    const dropdown = header?.querySelector('[data-js-header-search-dropdown]');
    const overlay = header?.querySelector('.tw-header-search-overlay');
    const predictiveComponent = dropdown?.querySelector('predictive-search-component');
    const predictiveInput = predictiveComponent?.querySelector('.search-input');

    if (
      !(searchInput instanceof HTMLInputElement) ||
      !dropdown ||
      !predictiveComponent ||
      !(predictiveInput instanceof HTMLInputElement)
    ) {
      return null;
    }

    const resultsContainer = predictiveComponent.querySelector('[ref="predictiveSearchResults"]');
    if (!resultsContainer) {
      return null;
    }

    return {
      searchForm,
      searchInput,
      dropdown,
      overlay,
      predictiveComponent,
      predictiveInput,
      resultsContainer,
    };
  }

  /**
   * Get mobile search control elements
   * @private
   */
  #getMobileSearchControls(header) {
    const mobileSearchToggle = header?.querySelector('[data-search-toggle]');
    const searchDrawer = header?.querySelector('search-drawer-component[data-search-drawer]');
    const mobileDrawerDialog = searchDrawer?.querySelector('dialog');
    const mobileSearchInput = searchDrawer?.querySelector('[data-js-mobile-search-input]');
    const mobileSearchForm = searchDrawer?.querySelector('[data-js-mobile-search-form]');
    const mobileClearButton = searchDrawer?.querySelector('[data-js-mobile-search-clear]');
    const mobileCloseButton = searchDrawer?.querySelector('[data-js-mobile-search-close]');
    const mobileMenuButton = searchDrawer?.querySelector('[data-js-mobile-search-menu]');
    const mobileCartButton = searchDrawer?.querySelector('[data-js-mobile-search-cart]');
    const mobilePredictiveComponent = searchDrawer?.querySelector('predictive-search-component');
    const mobileDrawerInner = searchDrawer?.querySelector('.tw-search-drawer__inner');
    const mobileFooter = searchDrawer?.querySelector('.predictive-search-form__footer.mobile-only');
    const mobileViewAllButton = searchDrawer?.querySelector(
      '.predictive-search-form__footer.mobile-only [ref="viewAllButton"]',
    );
    const mobileSearchIcon = searchDrawer?.querySelector('[data-js-mobile-search-icon]');
    const mobilePredictiveInput = mobilePredictiveComponent?.querySelector('.search-input');
    const mobileResultsContainer = mobilePredictiveComponent?.querySelector('[ref="predictiveSearchResults"]');
    const mobileDefaultResultsTemplate =
      mobileResultsContainer?.querySelector('[data-search-results]')?.cloneNode(true);
    const menuToggleButton = header?.querySelector('[data-menu-toggle]');
    const cartDrawerComponent = document.querySelector('cart-drawer-component');

    if (
      mobileSearchToggle &&
      searchDrawer &&
      mobileDrawerDialog instanceof HTMLDialogElement &&
      mobileSearchInput instanceof HTMLInputElement &&
      mobilePredictiveComponent &&
      mobilePredictiveInput instanceof HTMLInputElement &&
      mobileResultsContainer
    ) {
      return {
        mobileSearchToggle,
        searchDrawer,
        mobileDrawerDialog,
        mobileSearchInput,
        mobileSearchForm,
        mobileClearButton,
        mobileCloseButton,
        mobileMenuButton,
        mobileCartButton,
        mobilePredictiveComponent,
        mobileDrawerInner,
        mobileFooter,
        mobileViewAllButton,
        mobileSearchIcon,
        mobilePredictiveInput,
        mobileResultsContainer,
        mobileDefaultResultsTemplate,
        menuToggleButton,
        cartDrawerComponent,
      };
    }

    return null;
  }

  /**
   * Sync predictive input value with input event
   * @private
   */
  #syncPredictiveValue(predictiveInput, value, inputType = 'insertText') {
    predictiveInput.value = value;
    try {
      predictiveInput.dispatchEvent(
        new InputEvent('input', {
          bubbles: true,
          data: '',
          inputType,
        }),
      );
    } catch (error) {
      const fallbackEvent = new Event('input', { bubbles: true });
      try {
        Object.defineProperty(fallbackEvent, 'inputType', {
          value: inputType,
          configurable: true,
        });
        Object.defineProperty(fallbackEvent, 'data', {
          value: '',
          configurable: true,
        });
      } catch (defineError) {
        fallbackEvent.inputType = inputType;
        fallbackEvent.data = '';
      }
      predictiveInput.dispatchEvent(fallbackEvent);
    }
  }

  /**
   * Get current results state from results container
   * @private
   */
  #getResultsState(resultsContainer) {
    const resultsWrapper = resultsContainer.querySelector('[data-search-results]');
    if (!resultsWrapper) {
      return {
        hasRenderableResults: false,
        hasNoResults: false,
        isEmptyState: true,
      };
    }

    const isEmptyState = resultsWrapper.hasAttribute('data-empty-state');
    const hasNoResults = Boolean(resultsWrapper.querySelector('.predictive-search-results__no-results'));
    const hasRenderableResults = Boolean(
      resultsWrapper.querySelector(
        '[ref="resultsItems[]"], .predictive-search-results__card, .tw-search-modal__item',
      ),
    );

    return {
      hasRenderableResults,
      hasNoResults,
      isEmptyState,
    };
  }

  /**
   * Initialize desktop predictive search dropdown
   * @private
   */
  #initDesktopPredictiveSearch(controls) {
    const {
      searchForm,
      searchInput,
      dropdown,
      overlay,
      predictiveComponent,
      predictiveInput,
      resultsContainer,
    } = controls;

    let isHeaderDriven = false;
    let shouldOpenOnResults = false;
    let pendingOpenRequest = null;
    dropdown.style.visibility = 'hidden';
    dropdown.style.pointerEvents = 'none';

    const originalFocus = predictiveInput.focus.bind(predictiveInput);
    predictiveInput.focus = (...args) => {
      if (isHeaderDriven) return;
      originalFocus(...args);
    };

    let closeButtonHandlerBound = false;

    const bindDropdownCloseButton = () => {
      // Use event delegation on the dropdown/predictiveComponent to catch clicks
      // This works even if the button is replaced/re-rendered
      if (!closeButtonHandlerBound) {
        closeButtonHandlerBound = true;
        // Listen on both dropdown and component with capture phase
        [dropdown, predictiveComponent].forEach((container) => {
          if (!container) return;
          container.addEventListener('click', (event) => {
            const target = event.target;
            const closeButton = target?.closest?.('.predictive-search__close-modal-button');
            if (closeButton instanceof HTMLButtonElement) {
              event.preventDefault();
              event.stopImmediatePropagation();
              // Clear the header search inputs (desktop and mobile)
              const headerSearchInputs = document.querySelectorAll('.site-header__search-input, .site-header__mobile-search-input');
              headerSearchInputs.forEach((input) => {
                if (input instanceof HTMLInputElement) {
                  input.value = '';
                  // Update the clear button visibility if it exists
                  const clearButton = input.parentElement?.querySelector('[data-search-clear], [data-mobile-search-clear]');
                  if (clearButton instanceof HTMLElement) {
                    clearButton.style.display = 'none';
                  }
                }
              });
              closeDropdown(false);
            }
          }, { capture: true });
        });
      }
    };

    const setExpanded = (expanded) => {
      searchInput.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const headerGroup = document.getElementById('header-group');
      if (expanded) {
        dropdown.style.visibility = 'visible';
        dropdown.style.pointerEvents = 'auto';
        dropdown.dataset.open = 'true';
        if (overlay) overlay.dataset.open = 'true';
        if (headerGroup) headerGroup.dataset.searchOpen = 'true';
        document.documentElement.style.setProperty('overflow', 'hidden');
        document.body.style.setProperty('overflow', 'hidden');
        bindDropdownCloseButton();
      } else {
        dropdown.removeAttribute('data-open');
        dropdown.style.visibility = 'hidden';
        dropdown.style.pointerEvents = 'none';
        if (overlay) overlay.removeAttribute('data-open');
        if (headerGroup) headerGroup.removeAttribute('data-search-open');
        document.documentElement.style.removeProperty('overflow');
        document.body.style.removeProperty('overflow');
      }
    };

    const closeDropdown = (resetPredictive = true) => {
      setExpanded(false);
      if (resetPredictive && typeof predictiveComponent.resetSearch === 'function') {
        predictiveComponent.resetSearch(false);
      }
      isHeaderDriven = false;
      if (pendingOpenRequest) {
        window.clearTimeout(pendingOpenRequest);
        pendingOpenRequest = null;
      }
    };

    const updateDesktopLayoutMeasurements = () => {
      const layout = dropdown?.querySelector('.tw-search-modal__layout');
      if (!(layout instanceof HTMLElement)) return;
      const viewWidth = window.innerWidth;
      const navBreakpoint = parseInt(document.getElementById('site-header')?.dataset.navBreakpoint) || 1366;
      const prefersDesktop =
        layout.classList.contains('tw-search-modal__layout--no-results') ||
        viewWidth >= navBreakpoint;

      layout.toggleAttribute('data-desktop-layout', prefersDesktop);

      const resultsWrapper = layout.querySelector('[data-search-results]');
      if (!(resultsWrapper instanceof HTMLElement)) return;

      const cards = resultsWrapper.querySelectorAll('.custom-product-card__info');
      cards.forEach((card) => {
        if (!(card instanceof HTMLElement)) return;
        const shouldClamp = prefersDesktop;
        card.style.setProperty('--predictive-card-info-max-height', shouldClamp ? '18rem' : 'none');
      });
    };

    const openDropdown = () => {
      isHeaderDriven = true;
      const { hasRenderableResults, hasNoResults, isEmptyState } = this.#getResultsState(resultsContainer);
      if (!hasRenderableResults || hasNoResults || isEmptyState) {
        pendingOpenRequest = window.setTimeout(openDropdown, 30);
        return;
      }

      pendingOpenRequest = null;
      // If a desktop mega menu is open, close it before opening search
      if (typeof this.hideMegamenu === 'function') {
        this.hideMegamenu();
      }
      setExpanded(true);
      if (typeof predictiveComponent.expandSearch === 'function') {
        predictiveComponent.expandSearch();
      }
      requestAnimationFrame(() => {
        searchInput.focus({ preventScroll: true });
        updateDesktopLayoutMeasurements();
      });
    };

    const maybeOpenDropdown = () => {
      if (!shouldOpenOnResults) return;
      const { hasRenderableResults, hasNoResults, isEmptyState } = this.#getResultsState(resultsContainer);
      if (!hasRenderableResults || hasNoResults || isEmptyState) return;
      openDropdown();
    };

    const resultsObserver = new MutationObserver(() => {
      maybeOpenDropdown();
    });

    resultsObserver.observe(resultsContainer, {
      childList: true,
      subtree: true,
    });

    const handleInput = (event) => {
      const value = event.target.value;
      const trimmed = value.trim();
      const inputType =
        event instanceof InputEvent && event.inputType
          ? event.inputType
          : trimmed.length === 0
            ? 'deleteContentBackward'
            : 'insertText';

      this.#syncPredictiveValue(predictiveInput, value, inputType);

      if (trimmed.length > 0) {
        shouldOpenOnResults = true;
        openDropdown();
      } else {
        shouldOpenOnResults = false;
        closeDropdown();
      }
    };

    searchInput.addEventListener('focus', () => {
      if (searchInput.value.trim().length > 0) {
        shouldOpenOnResults = true;
        this.#syncPredictiveValue(predictiveInput, searchInput.value, 'insertText');
        openDropdown();
      }
    });

    searchInput.addEventListener('input', handleInput);

    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        searchInput.value = '';
        this.#syncPredictiveValue(predictiveInput, '', 'deleteContentBackward');
        shouldOpenOnResults = false;
        closeDropdown();
      }
    });

    searchInput.addEventListener('blur', () => {
      window.setTimeout(() => {
        const activeElement = document.activeElement;
        if (activeElement instanceof Node) {
          if (activeElement === searchInput) return;
          if (dropdown.contains(activeElement)) return;
        }
        closeDropdown(false);
      }, 100);
    });

    dropdown.addEventListener('pointerdown', () => {
      if (!isHeaderDriven) return;
      requestAnimationFrame(() => {
        searchInput.focus({ preventScroll: true });
      });
    });

    document.addEventListener('pointerdown', (event) => {
      if (!isHeaderDriven) return;
      const target = event.target;
      if (
        !(target instanceof Node) ||
        searchForm?.contains(target) ||
        dropdown.contains(target)
      ) {
        return;
      }
      closeDropdown();
      shouldOpenOnResults = searchInput.value.trim().length > 0;
    });

    searchForm?.addEventListener('submit', () => {
      closeDropdown(false);
      shouldOpenOnResults = false;
    });

    window.addEventListener('resize', updateDesktopLayoutMeasurements);
    window.addEventListener('orientationchange', updateDesktopLayoutMeasurements);
    updateDesktopLayoutMeasurements();
    predictiveComponent.addEventListener('predictive-search:results-updated', () => {
      bindDropdownCloseButton();
      requestAnimationFrame(() => {
        updateDesktopLayoutMeasurements();
      });
    });

    // Return handlers for breakpoint management
    return { closeDropdown };
  }

  /**
   * Initialize mobile predictive search drawer
   * @private
   */
  #initMobilePredictiveSearch(controls) {
    const {
      mobileSearchToggle,
      searchDrawer,
      mobileDrawerDialog,
      mobileSearchInput,
      mobileSearchForm,
      mobileClearButton,
      mobileCloseButton,
      mobileMenuButton,
      mobileCartButton,
      mobilePredictiveComponent,
      mobileDrawerInner,
      mobileFooter,
      mobileViewAllButton,
      mobileSearchIcon,
      mobilePredictiveInput,
      mobileResultsContainer,
      mobileDefaultResultsTemplate,
      menuToggleButton,
      cartDrawerComponent,
    } = controls;

    let resetOnClose = false;
    let shouldRestoreMobileDefaults = false;

    const mobileOriginalFocus = mobilePredictiveInput.focus.bind(mobilePredictiveInput);
    mobilePredictiveInput.focus = (...args) => {
      if (searchDrawer.hasAttribute('data-open')) return;
      mobileOriginalFocus(...args);
    };

    const updateMobileClearButton = () => {
      if (!mobileClearButton) return;
      const hasValue = mobileSearchInput.value.trim().length > 0;
      mobileClearButton.hidden = !hasValue;
      if (mobileSearchIcon) {
        mobileSearchIcon.hidden = hasValue;
        mobileSearchIcon.setAttribute('aria-hidden', hasValue ? 'true' : 'false');
        if (!hasValue) {
          mobileSearchIcon.removeAttribute('aria-hidden');
        }
      }
    };

    const restoreMobileDefaultResults = () => {
      if (!mobileDefaultResultsTemplate) return false;

      const defaultClone = mobileDefaultResultsTemplate.cloneNode(true);
      const existingWrapper = mobileResultsContainer.querySelector('[data-search-results]');

      if (existingWrapper) {
        existingWrapper.replaceWith(defaultClone);
      } else {
        mobileResultsContainer.innerHTML = '';
        mobileResultsContainer.appendChild(defaultClone);
      }

      return true;
    };

    const updateMobileFooterHeight = () => {
      if (!mobilePredictiveComponent) return;
      let footerHeight = 0;

      const resultsWrapper = mobileResultsContainer.querySelector('[data-search-results]');
      const hasNoResults = Boolean(resultsWrapper?.querySelector('.predictive-search-results__no-results'));
      const hasRenderableResults = Boolean(
        resultsWrapper?.querySelector(
          '[ref="resultsItems[]"], .predictive-search-results__card, .tw-search-modal__item',
        ),
      );
      const isEmptyState = Boolean(resultsWrapper?.hasAttribute('data-empty-state'));
      const shouldApplyMaxHeight = hasRenderableResults && !hasNoResults && !isEmptyState;

      if (mobileFooter instanceof HTMLElement) {
        const isVisible = window.getComputedStyle(mobileFooter).display !== 'none';
        const rectHeight = mobileFooter.getBoundingClientRect().height;
        footerHeight = isVisible ? Math.max(mobileFooter.offsetHeight, rectHeight) : 0;
        if (shouldApplyMaxHeight && footerHeight === 0) {
          window.setTimeout(updateMobileFooterHeight, 60);
        }
      }

      mobilePredictiveComponent.style.setProperty('--mobile-search-footer-height', `${footerHeight}px`);
      mobilePredictiveComponent.dataset.mobileSearchFooterHeight = `${footerHeight}`;
      mobilePredictiveComponent.dataset.mobileHasResults = shouldApplyMaxHeight ? 'true' : 'false';

      if (mobileDrawerInner instanceof HTMLElement) {
        mobileDrawerInner.style.setProperty('--mobile-search-footer-height', `${footerHeight}px`);
        mobileDrawerInner.dataset.mobileSearchFooterHeight = `${footerHeight}`;
        if (shouldApplyMaxHeight) {
          mobileDrawerInner.setAttribute('data-mobile-results', 'true');
        } else {
          mobileDrawerInner.removeAttribute('data-mobile-results');
        }
      }
    };

    const mobileResetPredictive = () => {
      if (typeof mobilePredictiveComponent.resetSearch === 'function') {
        shouldRestoreMobileDefaults = Boolean(mobileDefaultResultsTemplate);
        mobilePredictiveComponent.resetSearch(false);
      } else if (mobileDefaultResultsTemplate) {
        if (restoreMobileDefaultResults()) {
          requestAnimationFrame(() => {
            updateMobileFooterHeight();
          });
        }
      }
      mobilePredictiveComponent.style.setProperty('--mobile-search-footer-height', '0px');
      mobilePredictiveComponent.dataset.mobileSearchFooterHeight = '0';
      mobilePredictiveComponent.dataset.mobileHasResults = 'false';
      if (mobileDrawerInner instanceof HTMLElement) {
        mobileDrawerInner.style.setProperty('--mobile-search-footer-height', '0px');
        mobileDrawerInner.dataset.mobileSearchFooterHeight = '0';
        mobileDrawerInner.removeAttribute('data-mobile-results');
      }

      if (shouldRestoreMobileDefaults) {
        window.setTimeout(() => {
          if (!shouldRestoreMobileDefaults) return;
          const restored = restoreMobileDefaultResults();
          shouldRestoreMobileDefaults = false;
          if (restored) {
            requestAnimationFrame(() => {
              updateMobileFooterHeight();
            });
          }
        }, 150);
      }
    };

    const resetMobileState = () => {
      mobileSearchInput.value = '';
      updateMobileClearButton();
      this.#syncPredictiveValue(mobilePredictiveInput, '', 'deleteContentBackward');
      mobileResetPredictive();
    };

    const handleDrawerOpen = () => {
      resetOnClose = false;
      searchDrawer.dataset.open = 'true';
      const headerGroup = document.getElementById('header-group');
      if (headerGroup) headerGroup.dataset.searchOpen = 'true';
      requestAnimationFrame(() => {
        mobileSearchInput.focus({ preventScroll: true });
        if (mobileSearchInput.value.trim().length > 0) {
          this.#syncPredictiveValue(mobilePredictiveInput, mobileSearchInput.value, 'insertText');
        }
        updateMobileClearButton();
        updateMobileFooterHeight();
      });
    };

    const handleDrawerClose = () => {
      searchDrawer.removeAttribute('data-open');
      const headerGroup = document.getElementById('header-group');
      if (headerGroup) headerGroup.removeAttribute('data-search-open');
      if (resetOnClose) {
        resetMobileState();
        resetOnClose = false;
      }
      mobilePredictiveComponent.style.setProperty('--mobile-search-footer-height', '0px');
      mobilePredictiveComponent.dataset.mobileSearchFooterHeight = '0';
      mobilePredictiveComponent.dataset.mobileHasResults = 'false';
      if (mobileDrawerInner instanceof HTMLElement) {
        mobileDrawerInner.style.setProperty('--mobile-search-footer-height', '0px');
        mobileDrawerInner.dataset.mobileSearchFooterHeight = '0';
        mobileDrawerInner.removeAttribute('data-mobile-results');
      }
    };

    const openMobileDrawer = () => {
      resetOnClose = false;
      if (typeof searchDrawer.open === 'function') {
        searchDrawer.open();
      } else if (typeof searchDrawer.showDialog === 'function') {
        searchDrawer.showDialog();
      } else if (!mobileDrawerDialog.open) {
        mobileDrawerDialog.showModal();
        handleDrawerOpen();
      }
    };

    const closeMobileDrawer = (reset = false) => {
      resetOnClose = reset;
      if (typeof searchDrawer.close === 'function') {
        searchDrawer.close();
      } else if (typeof searchDrawer.closeDialog === 'function') {
        searchDrawer.closeDialog();
      } else if (mobileDrawerDialog.open) {
        mobileDrawerDialog.close();
        handleDrawerClose();
      }
    };

    searchDrawer.addEventListener('search-drawer:open', handleDrawerOpen);
    searchDrawer.addEventListener('search-drawer:close', handleDrawerClose);
    mobileDrawerDialog.addEventListener('close', handleDrawerClose);

    const handleMobileInput = (event) => {
      const value = event.target.value;
      const trimmed = value.trim();
      const inputType =
        event instanceof InputEvent && event.inputType
          ? event.inputType
          : trimmed.length === 0
            ? 'deleteContentBackward'
            : 'insertText';

      this.#syncPredictiveValue(mobilePredictiveInput, value, inputType);
      updateMobileClearButton();

      if (trimmed.length > 0) {
        if (typeof mobilePredictiveComponent.expandSearch === 'function') {
          mobilePredictiveComponent.expandSearch();
        }
      } else {
        mobileResetPredictive();
      }
    };

    mobileSearchToggle.addEventListener('click', (e) => {
      if (searchDrawer.hasAttribute('data-open')) {
        closeMobileDrawer(true);
      } else {
        openMobileDrawer();
      }
    });

    mobileCloseButton?.addEventListener('click', () => {
      closeMobileDrawer(true);
    });

    mobileMenuButton?.addEventListener('click', () => {
      closeMobileDrawer(false);
      menuToggleButton?.dispatchEvent(new Event('click', { bubbles: true }));
    });

    mobileCartButton?.addEventListener('click', () => {
      closeMobileDrawer(false);
      if (cartDrawerComponent && typeof cartDrawerComponent.open === 'function') {
        cartDrawerComponent.open();
      } else if (mobileCartButton.dataset.cartUrl) {
        window.location.href = mobileCartButton.dataset.cartUrl;
      }
    });

    mobileSearchInput.addEventListener('input', handleMobileInput);
    mobileSearchInput.addEventListener('focus', () => {
      if (mobileSearchInput.value.trim().length > 0) {
        this.#syncPredictiveValue(mobilePredictiveInput, mobileSearchInput.value, 'insertText');
      }
    });

    mobileSearchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeMobileDrawer(true);
      }
    });

    mobileClearButton?.addEventListener('click', () => {
      mobileSearchInput.value = '';
      updateMobileClearButton();
      this.#syncPredictiveValue(mobilePredictiveInput, '', 'deleteContentBackward');
      mobileResetPredictive();
      window.setTimeout(() => {
        mobileSearchInput.focus({ preventScroll: true });
      }, 200);
    });

    mobileResultsContainer.addEventListener('pointerdown', () => {
      requestAnimationFrame(() => {
        mobileSearchInput.focus({ preventScroll: true });
      });
    });

    mobilePredictiveComponent.addEventListener('predictive-search:item-selected', () => {
      closeMobileDrawer(false);
    });

    mobilePredictiveComponent.addEventListener('predictive-search:results-updated', () => {
      updateMobileClearButton();

      if (shouldRestoreMobileDefaults) {
        const restored = restoreMobileDefaultResults();
        shouldRestoreMobileDefaults = false;
        if (restored) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              updateMobileFooterHeight();
            });
          });
          return;
        }
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateMobileFooterHeight();
        });
      });
    });

    mobileSearchForm?.addEventListener('submit', () => {
      closeMobileDrawer(false);
    });

    const mobileNavigateToSearch = () => {
      const query = mobileSearchInput.value.trim() || mobilePredictiveInput.value.trim();
      const searchRoute = window.Theme?.routes?.search_url ?? '/search';
      const searchUrl = new URL(searchRoute, window.location.origin);
      if (query.length > 0) {
        searchUrl.searchParams.set('q', query);
      }
      window.location.href = searchUrl.toString();
    };

    mobileViewAllButton?.addEventListener('click', (event) => {
      event.preventDefault();
      mobileNavigateToSearch();
    });

    updateMobileClearButton();
    updateMobileFooterHeight();

    // Return handlers for breakpoint management
    return { closeMobileDrawer, openMobileDrawer };
  }

  /**
   * Setup breakpoint handler to close appropriate search UI
   * @private
   */
  #setupBreakpointHandler(desktopHandlers, mobileHandlers) {
    if (!desktopHandlers || !mobileHandlers) return;

    const { closeDropdown } = desktopHandlers;
    const { closeMobileDrawer } = mobileHandlers;

    const breakpointHandler = () => {
      const siteHeader = document.getElementById('site-header');
      const navBreakpointPx = parseInt(siteHeader?.dataset.navBreakpoint) || 1366;
      const isMobile = window.innerWidth < navBreakpointPx;

      if (isMobile) {
        closeDropdown(false);
      } else {
        closeMobileDrawer(false);
      }
    };

    window.addEventListener('resize', breakpointHandler);
    window.addEventListener('orientationchange', breakpointHandler);
    breakpointHandler();
  }
}

// Initialize once
(function initOnce() {
  const init = () => {
    if (window.__SITE_HEADER_INIT__) return;
    window.__SITE_HEADER_INIT__ = true;
    
    new SiteHeader();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();